// theme.ts (tsx file with usage of StyleFunctions, see 4.)
import { extendTheme } from '@chakra-ui/react'

const breakpoints = {
  base: '0em', // 0px
  sm: '30em', // ~480px. em is a relative unit and is dependant on the font size.
  md: '48em', // ~768px
  lg: '62em', // ~992px
  xl: '80em', // ~1280px
  '2xl': '96em', // ~1536px
  '3xl': '200em'

}
const theme = extendTheme({
  // colors: colors,
  fonts: {
    heading: 'Inter, sans-serif',
    body: 'Inter, sans-serif',
    handwritten: 'Caveat Variable, cursive',
    emoji: 'Noto Color Emoji, sans-serif'
  },
  breakpoints,
  components: {
    Heading: {
      baseStyle: {
        fontSize: { base: '20px', lg: '56px' },
        lineHeight: '1.2',
        color: 'gray.700',
        letterSpacing: '-0.05em',
        mb: '0.5em'
      },
      variants: {
        emoji: {
          fontFamily: 'emoji',
          fontSize: '6xl',
          textShadow:
            '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          filter: 'url(#blur-and-scale)'
        }
      }
    },
    List: {
      parts: ['container', 'item', 'icon'],
      baseStyle: {
        item: {
          mb: '0.5em'
        }
      }
    },
    Text: {
      baseStyle: {
        color: 'gray.700',
        fontWeight: '500',
        fontSize: { base: '18px', lg: '24px' },
        lineHeight: '1.1',
        letterSpacing: '-0.05em',
        mb: '1em'
      },
      variants: {
        smaller: {
          fontWeight: '400',
          fontSize: { base: '16px', lg: '20px' },
          lineHeight: '1.25'
        },
        caption: {
          fontWeight: '400',
          fontSize: { base: '14px', lg: '14px' },
          lineHeight: '1.25',
          mt: '0.25em',
          mb: '0.5em',
          color: 'gray.600',
          display: 'block',
          a: {
            textDecoration: 'underline'
          }
        },
        blogDate: {
          fontWeight: '400',
          fontSize: { base: '16px', lg: '16px' },
          lineHeight: '1.25',
          mt: '0.25em',
          mb: '2.5em',
          color: 'gray.600'
        }
      }
    },
    Button: {
      variants: {
        outlineNegative: {
          fontWeight: '500',
          color: 'gray.700',
          bg: 'none',
          border: '2px solid',
          borderColor: 'gray.700',
          '&.active, &:hover:not([disabled])': {
            bg: 'gray.700',
            color: 'yellow.300',
            svg: {
              color: 'yellow.300'
            }
          },
          '&:disabled': {
            backgroundColor: 'gray.200'
          }
        },
        circle: {
          fontWeight: '800',
          color: 'gray.700',
          backgroundColor: 'yellow.400',
          '&:hover': {
            bg: 'yellow.300'
          }
        },
        solid: {
          fontWeight: '800',
          color: 'gray.700',
          backgroundColor: 'yellow.400',
          border: '2px solid',
          borderColor: 'gray.700',
          '&.active, &:hover:not([disabled])': {
            bg: 'gray.700',
            color: 'yellow.300',
            svg: {
              color: 'yellow.300'
            }
          },
          '&:disabled': {
            opacity: 0.5
          }
        },
        solidBuilder: {
          height: '2.5em',
          fontSize: {
            base: '10px',
            '2xl': '14px',
            '3xl': '16px'
          },
          fontWeight: '800',
          color: 'gray.700',
          backgroundColor: 'yellow.400',
          border: '2px solid',
          borderColor: 'gray.700',
          svg: {
            minWidth: '1.75em',
            minHeight: '1.75em'
          },
          '&.active, &:hover:not([disabled])': {
            bg: 'gray.700',
            color: 'yellow.300',
            svg: {
              color: 'yellow.300'
            }
          },
          '&:disabled': {
            opacity: 0.5
          }
        }

      }
    },
    Link: {
      variants: {
        stickyNote: {
          fontWeight: '500',
          p: '3px 10px',
          display: 'inline-flex',
          position: 'relative',
          flexDir: 'column',
          justifyContent: 'center',
          textDecoration: 'none',
          '&': {
            bg: 'yellow.300',
            position: 'relative',
            // color: 'white',
            '& .bend': {
              position: 'absolute',
              bg: 'white',
              bottom: '0px',
              right: '0px',
              borderStyle: 'solid',
              h: 'auto',
              aspectRatio: '1/1',
              w: '10px',
              boxShadow: '0px 2px 0px 0px white, 2px 0px 0px 0px white',
              zIndex: 2,
              overflow: 'hidden',
              '&:after': {
                pointerEvents: 'none',
                color: 'yellow.100',
                background:
                  'linear-gradient(135deg, currentcolor calc(50% - 1px), rgba(232,232,232,1) 50%, rgba(255,255,255,0) 50.2%, rgba(255,255,255,0) 100%)',
                content: '""',
                display: 'block',
                pos: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0
              },
              button: {
                pos: 'absolute',
                right: '20px',
                bottom: '20px'
              }
            },
            svg: {
              fill: 'white'
            }
          }
        }
      }
    },
    Icon: {
      baseStyle: {
        color: 'gray.700'
      }
    },
    Modal: {
      parts: ['overlay', 'dialog', 'body'],
      baseStyle: {
        overlay: {
          zIndex: '38',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
          overflowY: 'auto',
          p: '60px',
          background: 'rgba(0,0,0,0.75)'
        },
        dialog: {
          backgroundColor: 'yellow.300',
          w: { base: '70vw' },
          h: { base: 'auto', lg: '70vw' },
          maxH: '80vh',

          maxWidth: '900px',
          borderRadius: 0
        },
        body: {
          position: 'relative',
          p: '20px',
          a: {
            textDecoration: 'underline !important'
          }
        }
      }
    }
  }
})

export default theme
